import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users,
    FileText,
    Tag,
    CheckCircle2,
    XCircle,
    Plus,
    Trash2,
    Upload,
    Loader2,
    Pencil,
    Paperclip,
    X,
    Zap,
    Leaf,
    Mail,
    Euro,
    Send,
    KeyRound,
    Megaphone,
    Copy,
    Phone,
    Search,
    Briefcase,
} from "lucide-react";
import { api, API } from "../lib/api";
import { SECTORS, SECTOR_LABELS } from "../data/wizardConfig";
import { toast } from "sonner";

import AdminContracts from "./AdminContracts";

const TABS = [
    { key: "users", label: "Utenti", icon: Users },
    { key: "quotes", label: "Richieste", icon: FileText },
    { key: "contracts", label: "Contratti", icon: Briefcase },
    { key: "offers", label: "Catalogo offerte", icon: Tag },
    { key: "energy", label: "Energia", icon: Zap },
];

export default function AdminDashboard() {
    const [tab, setTab] = useState("users");
    const [emailTesting, setEmailTesting] = useState(false);

    const testEmail = async () => {
        if (emailTesting) return;
        setEmailTesting(true);
        try {
            const { data } = await api.post("/admin/email/test", {});
            toast.success(`Email inviata a ${data.to}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invio fallito");
        } finally {
            setEmailTesting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto" data-testid="admin-dashboard">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs tracking-widest font-bold text-[#898A8D]">
                        AREA AMMINISTRATORE
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black text-[#0C4650] mt-1">
                        Control room Scintilla Hub
                    </h1>
                </div>
                <button
                    onClick={testEmail}
                    disabled={emailTesting}
                    className="btn-outline !py-2 !px-3 !text-xs w-fit"
                    data-testid="test-email-btn"
                >
                    {emailTesting ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                        <Mail className="w-3 h-3" />
                    )}
                    Test Email
                </button>
            </div>

            <div className="flex gap-2 mb-6 overflow-x-auto">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`admin-tab-${t.key}`}
                        className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm transition-all ${
                            tab === t.key
                                ? "bg-[#0C4650] text-white"
                                : "bg-white border border-gray-200 text-[#0C4650] hover:border-[#0C4650]"
                        }`}
                    >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "users" && <UsersPanel />}
            {tab === "quotes" && <QuotesPanel />}
            {tab === "contracts" && <AdminContracts />}
            {tab === "offers" && <OffersPanel />}
            {tab === "energy" && <EnergyOffersPanel />}
        </div>
    );
}

// --------- USERS ----------
const SECTOR_TOGGLES = [
    { key: "noleggio_lungo_termine", label: "Noleggio L.T.", short: "NLT" },
    { key: "energy_quoter", label: "Energy Quoter", short: "EQ" },
];
// Settori abilitati di default per i nuovi agenti (Energy Quoter è opt-in)
const DEFAULT_AGENT_SECTORS = [
    "noleggio_lungo_termine",
];

const UsersPanel = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);
    const [broadcastOpen, setBroadcastOpen] = useState(false);

    const load = () =>
        api.get("/admin/users").then(({ data }) => {
            setUsers(data);
            setLoading(false);
        });
    useEffect(() => {
        load();
    }, []);

    const setStatus = async (id, status, allowedSectors) => {
        const body = { status };
        if (allowedSectors) body.allowed_sectors = allowedSectors;
        await api.patch(`/admin/users/${id}/status`, body);
        toast.success(
            `Utente ${status === "approved" ? "approvato" : status === "rejected" ? "rifiutato" : "aggiornato"}`,
        );
        load();
    };

    const setSectors = async (id, allowedSectors) => {
        await api.patch(`/admin/users/${id}/sectors`, { allowed_sectors: allowedSectors });
        setUsers((prev) =>
            prev.map((u) =>
                u.id === id ? { ...u, allowed_sectors: allowedSectors } : u,
            ),
        );
        toast.success("Settori aggiornati");
    };

    const onProfileSaved = (updated) => {
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    };

    const pending = users.filter((u) => u.status === "pending");
    const approved = users.filter((u) => u.status === "approved");
    const rejected = users.filter((u) => u.status === "rejected");
    const approvedAgents = approved.filter((u) => u.role !== "admin");

    return (
        <div className="space-y-6">
            {/* --- Comunicazioni broadcast --- */}
            <div className="card p-5 bg-gradient-to-br from-[#0C4650] to-[#0a3a42] text-white"
                 data-testid="broadcast-card">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#C7FF2C] flex items-center justify-center text-[#0C4650] shrink-0">
                            <Megaphone className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black">Invia comunicazione agli agenti</h3>
                            <p className="text-sm text-[#91C6C0] mt-0.5 max-w-xl">
                                Messaggi generici, offerte o aggiornamenti. Notifica in-app +
                                email a tutti gli agenti attivi (o a una selezione).
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setBroadcastOpen(true)}
                        data-testid="open-broadcast-btn"
                        className="inline-flex items-center justify-center gap-2 bg-[#C7FF2C] hover:bg-[#bff21d] text-[#0C4650] font-bold px-5 py-3 rounded-xl transition-all whitespace-nowrap"
                    >
                        <Send className="w-4 h-4" /> Nuovo messaggio
                    </button>
                </div>
            </div>

            <Section title={`In attesa (${pending.length})`} testid="pending-users-section">
                {loading ? (
                    <Loading />
                ) : pending.length === 0 ? (
                    <Empty text="Nessuna richiesta in attesa." />
                ) : (
                    pending.map((u) => (
                        <UserRow
                            key={u.id}
                            user={u}
                            onApprove={(sectors) => setStatus(u.id, "approved", sectors)}
                            onReject={() => setStatus(u.id, "rejected")}
                            onSectorsChange={(sectors) => setSectors(u.id, sectors)}
                            onEdit={() => setEditUser(u)}
                        />
                    ))
                )}
            </Section>
            <Section title={`Attivi (${approved.length})`} testid="approved-users-section">
                {approved.map((u) => (
                    <UserRow
                        key={u.id}
                        user={u}
                        onReject={() => setStatus(u.id, "rejected")}
                        onSectorsChange={(sectors) => setSectors(u.id, sectors)}
                        onEdit={() => setEditUser(u)}
                    />
                ))}
            </Section>
            {rejected.length > 0 && (
                <Section title={`Rifiutati (${rejected.length})`} testid="rejected-users-section">
                    {rejected.map((u) => (
                        <UserRow
                            key={u.id}
                            user={u}
                            onApprove={(sectors) => setStatus(u.id, "approved", sectors)}
                            onEdit={() => setEditUser(u)}
                        />
                    ))}
                </Section>
            )}

            {editUser && (
                <UserEditModal
                    user={editUser}
                    onClose={() => setEditUser(null)}
                    onSaved={(updated) => {
                        onProfileSaved(updated);
                        setEditUser(null);
                    }}
                />
            )}
            {broadcastOpen && (
                <BroadcastModal
                    agents={approvedAgents}
                    onClose={() => setBroadcastOpen(false)}
                />
            )}
        </div>
    );
};

const UserRow = ({ user, onApprove, onReject, onSectorsChange, onEdit }) => {
    const isAdmin = user.role === "admin";
    // Local draft state of sectors — for pending users: picks; for approved: saved on blur/change
    const initialSectors = user.allowed_sectors ?? DEFAULT_AGENT_SECTORS;
    const [selected, setSelected] = useState(initialSectors);

    // Re-sync when parent refreshes users list
    useEffect(() => {
        setSelected(user.allowed_sectors ?? DEFAULT_AGENT_SECTORS);
    }, [user.allowed_sectors]);

    const toggle = (key) => {
        const next = selected.includes(key)
            ? selected.filter((k) => k !== key)
            : [...selected, key];
        setSelected(next);
        // Persist immediately for approved users (inline editing)
        if (!isAdmin && user.status === "approved" && onSectorsChange) {
            onSectorsChange(next);
        }
    };

    return (
        <div className="card p-4" data-testid={`user-row-${user.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-[#0C4650] text-[#C7FF2C] flex items-center justify-center font-black">
                        {user.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="font-bold text-[#0C4650] truncate">{user.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                            {user.email} {isAdmin && "· admin"}
                        </div>
                        {user.phone && <div className="text-xs text-gray-500">{user.phone}</div>}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge-${user.status}`}>{user.status}</span>
                    {!isAdmin && onEdit && (
                        <button
                            onClick={onEdit}
                            className="btn-outline !py-2 !px-3 !text-sm"
                            data-testid={`edit-user-${user.id}`}
                            title="Modifica profilo o reimposta password"
                        >
                            <Pencil className="w-4 h-4" /> Modifica
                        </button>
                    )}
                    {!isAdmin && onApprove && user.status !== "approved" && (
                        <button
                            onClick={() => onApprove(selected)}
                            className="btn-primary !py-2 !px-4 !text-sm"
                            data-testid={`approve-user-${user.id}`}
                        >
                            <CheckCircle2 className="w-4 h-4" /> Approva
                        </button>
                    )}
                    {!isAdmin && onReject && user.status !== "rejected" && (
                        <button
                            onClick={onReject}
                            className="btn-outline !py-2 !px-4 !text-sm"
                            data-testid={`reject-user-${user.id}`}
                        >
                            <XCircle className="w-4 h-4" /> Rifiuta
                        </button>
                    )}
                </div>
            </div>
            {!isAdmin && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-[11px] tracking-widest font-bold text-[#898A8D]">
                            SETTORI ABILITATI
                        </div>
                        <div className="text-[11px] text-gray-400">
                            {selected.length}/{SECTOR_TOGGLES.length} attivi
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SECTOR_TOGGLES.map((s) => {
                            const active = selected.includes(s.key);
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => toggle(s.key)}
                                    data-testid={`user-${user.id}-sector-${s.key}`}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                                        active
                                            ? "bg-[#C7FF2C] text-[#0C4650] border-2 border-[#C7FF2C]"
                                            : "bg-white text-gray-400 border-2 border-gray-200 hover:border-[#0C4650] hover:text-[#0C4650]"
                                    }`}
                                >
                                    <span
                                        className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-black ${
                                            active
                                                ? "bg-[#0C4650] text-[#C7FF2C]"
                                                : "bg-gray-100 text-gray-400"
                                        }`}
                                    >
                                        {active ? "✓" : ""}
                                    </span>
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                    {user.status === "pending" && (
                        <p className="mt-2 text-[11px] text-gray-500">
                            Scegli i settori da rendere visibili per questo agente prima di approvarlo.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// --------- USER EDIT MODAL ----------
const UserEditModal = ({ user, onClose, onSaved }) => {
    const [form, setForm] = useState({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        company: user.company || "",
    });
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [customPw, setCustomPw] = useState("");
    const [sendEmail, setSendEmail] = useState(true);
    const [generatedPw, setGeneratedPw] = useState(null);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const saveProfile = async () => {
        if (saving) return;
        if (!form.name || form.name.trim().length < 2) {
            toast.error("Inserisci un nome valido");
            return;
        }
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("Inserisci una email valida");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/users/${user.id}/profile`, {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                company: form.company.trim(),
            });
            toast.success("Profilo aggiornato");
            onSaved(data.user);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Salvataggio fallito");
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async () => {
        if (resetting) return;
        if (customPw && customPw.length < 8) {
            toast.error("La password deve avere almeno 8 caratteri");
            return;
        }
        if (!window.confirm(
            customPw
                ? "Confermi la nuova password personalizzata per questo utente?"
                : "Confermi di generare una password temporanea per questo utente?"
        )) return;
        setResetting(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/reset-password`, {
                new_password: customPw || null,
                send_email: sendEmail,
            });
            setGeneratedPw(data.temp_password);
            toast.success(
                data.email_sent
                    ? "Password reimpostata e inviata via email"
                    : "Password reimpostata"
            );
            setCustomPw("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset fallito");
        } finally {
            setResetting(false);
        }
    };

    const copyPw = () => {
        if (!generatedPw) return;
        navigator.clipboard.writeText(generatedPw);
        toast.success("Password copiata");
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
             onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="user-edit-modal">
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-black text-[#0C4650]">Modifica utente</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                    </div>
                    <button onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            data-testid="close-user-edit-modal">
                        <X className="w-5 h-5 text-[#0C4650]" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Dati anagrafici */}
                    <div>
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-3">
                            DATI PERSONALI
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Nome completo *
                                </label>
                                <input
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    data-testid="edit-user-name"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    data-testid="edit-user-email"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Telefono
                                </label>
                                <input
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    placeholder="+39 333 1234567"
                                    value={form.phone}
                                    onChange={(e) => set("phone", e.target.value)}
                                    data-testid="edit-user-phone"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Azienda
                                </label>
                                <input
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    value={form.company}
                                    onChange={(e) => set("company", e.target.value)}
                                    data-testid="edit-user-company"
                                />
                            </div>
                        </div>
                        <button
                            onClick={saveProfile}
                            disabled={saving}
                            className="btn-primary w-full mt-4 disabled:opacity-50"
                            data-testid="save-user-profile-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Salva modifiche
                        </button>
                    </div>

                    {/* Reset password */}
                    <div className="border-t border-gray-100 pt-5">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-3">
                            RESET PASSWORD
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Password personalizzata (opzionale)
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    placeholder="Lascia vuoto per generarla automaticamente"
                                    value={customPw}
                                    onChange={(e) => setCustomPw(e.target.value)}
                                    data-testid="custom-password-input"
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                    Minimo 8 caratteri. Se vuoto, ne generiamo una sicura.
                                </p>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-[#0C4650] cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                    className="rounded"
                                    data-testid="send-email-reset-toggle"
                                />
                                Invia la nuova password via email all'utente
                            </label>
                            <button
                                onClick={resetPassword}
                                disabled={resetting}
                                className="btn-outline w-full disabled:opacity-50 border-amber-300 text-amber-700 hover:bg-amber-50"
                                data-testid="reset-user-password-btn"
                            >
                                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                                Reimposta password
                            </button>
                            {generatedPw && (
                                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3"
                                     data-testid="generated-password-box">
                                    <div className="text-[10px] tracking-widest font-bold text-amber-700 mb-1">
                                        NUOVA PASSWORD
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 font-mono text-base font-bold text-[#0C4650] break-all">
                                            {generatedPw}
                                        </code>
                                        <button onClick={copyPw}
                                                className="p-2 hover:bg-amber-100 rounded-lg"
                                                title="Copia">
                                            <Copy className="w-4 h-4 text-amber-700" />
                                        </button>
                                    </div>
                                    <p className="text-[11px] text-amber-700 mt-2">
                                        Comunicala all'utente in modo sicuro. Visibile solo ora.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --------- BROADCAST MODAL ----------
const BROADCAST_TEMPLATES = [
    {
        key: "promo",
        label: "Nuova offerta promo",
        subject: "Nuova offerta disponibile su Scintilla Hub",
        message: "Ciao! Abbiamo appena pubblicato una nuova offerta promozionale che puoi proporre ai tuoi clienti. Accedi al catalogo per scoprire i dettagli.",
        cta_label: "Vai al catalogo",
        cta_url: "/offerte",
    },
    {
        key: "training",
        label: "Aggiornamento / Formazione",
        subject: "Aggiornamento operativo Scintilla Hub",
        message: "Abbiamo introdotto alcuni miglioramenti alla piattaforma per rendere il tuo lavoro più rapido. Dai un'occhiata alle novità sulla dashboard.",
        cta_label: "Apri la dashboard",
        cta_url: "/dashboard",
    },
    {
        key: "reminder",
        label: "Promemoria generico",
        subject: "Promemoria importante",
        message: "Ti ricordiamo di completare i preventivi in sospeso entro fine settimana. Per qualsiasi necessità contatta la sede.",
        cta_label: "Vai alle richieste",
        cta_url: "/le-mie-richieste",
    },
];

const BroadcastModal = ({ agents, onClose }) => {
    const [form, setForm] = useState({
        subject: "",
        message: "",
        cta_label: "",
        cta_url: "",
        audience: "all_approved",
        send_email: true,
    });
    const [selected, setSelected] = useState([]);
    const [sending, setSending] = useState(false);
    const [attachment, setAttachment] = useState(null); // {url, filename, size}
    const [uploadingAtt, setUploadingAtt] = useState(false);
    const attRef = useRef(null);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleAttachment = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) {
            toast.error("File troppo grande (max 25 MB)");
            return;
        }
        setUploadingAtt(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload-attachment", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            setAttachment({
                url: data.url,
                filename: data.filename || file.name,
                size: data.size || file.size,
            });
            toast.success("Allegato caricato");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload allegato fallito");
        } finally {
            setUploadingAtt(false);
        }
    };

    const removeAttachment = () => setAttachment(null);

    const fmtSize = (s) => {
        if (!s) return "";
        if (s < 1024) return s + " B";
        if (s < 1024 * 1024) return (s / 1024).toFixed(0) + " KB";
        return (s / 1024 / 1024).toFixed(1) + " MB";
    };

    const toggleAgent = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const applyTemplate = (tpl) => {
        setForm((p) => ({
            ...p,
            subject: tpl.subject,
            message: tpl.message,
            cta_label: tpl.cta_label,
            cta_url: tpl.cta_url,
        }));
    };

    const recipientCount =
        form.audience === "specific"
            ? selected.length
            : agents.length;

    const send = async () => {
        if (sending) return;
        if (!form.subject.trim() || form.subject.trim().length < 2) {
            toast.error("Inserisci un oggetto");
            return;
        }
        if (!form.message.trim() || form.message.trim().length < 2) {
            toast.error("Inserisci un messaggio");
            return;
        }
        if (form.audience === "specific" && selected.length === 0) {
            toast.error("Seleziona almeno un destinatario");
            return;
        }
        if (!window.confirm(
            `Inviare la comunicazione a ${recipientCount} agente${recipientCount === 1 ? "" : "i"}?`
        )) return;
        setSending(true);
        try {
            const payload = {
                subject: form.subject.trim(),
                message: form.message.trim(),
                cta_label: form.cta_label.trim() || null,
                cta_url: form.cta_url.trim() || null,
                audience: form.audience,
                send_email: form.send_email,
            };
            if (form.audience === "specific") payload.user_ids = selected;
            if (attachment) {
                payload.attachment_url = attachment.url;
                payload.attachment_name = attachment.filename;
            }
            const { data } = await api.post("/admin/broadcast", payload);
            toast.success(
                `Inviato a ${data.recipients_count} agente/i · ${data.emails_sent} email`
            );
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invio fallito");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
             onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="broadcast-modal">
                <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#C7FF2C] flex items-center justify-center text-[#0C4650]">
                            <Megaphone className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-[#0C4650]">Nuova comunicazione</h3>
                            <p className="text-xs text-gray-500">Notifica in-app + email</p>
                        </div>
                    </div>
                    <button onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            data-testid="close-broadcast-modal">
                        <X className="w-5 h-5 text-[#0C4650]" />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Template suggeriti */}
                    <div>
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-2">
                            TEMPLATE RAPIDI
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {BROADCAST_TEMPLATES.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => applyTemplate(t)}
                                    data-testid={`broadcast-template-${t.key}`}
                                    className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#0C4650]/5 text-[#0C4650] hover:bg-[#C7FF2C] transition-all"
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Destinatari */}
                    <div>
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] mb-2">
                            DESTINATARI
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <button
                                onClick={() => set("audience", "all_approved")}
                                data-testid="broadcast-audience-all"
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                    form.audience === "all_approved"
                                        ? "bg-[#0C4650] text-white border-[#0C4650]"
                                        : "bg-white text-[#0C4650] border-gray-200 hover:border-[#0C4650]"
                                }`}
                            >
                                <div className="text-sm font-bold">Tutti gli agenti attivi</div>
                                <div className={`text-xs mt-0.5 ${
                                    form.audience === "all_approved" ? "text-[#C7FF2C]" : "text-gray-500"
                                }`}>
                                    {agents.length} agente/i
                                </div>
                            </button>
                            <button
                                onClick={() => set("audience", "specific")}
                                data-testid="broadcast-audience-specific"
                                className={`p-3 rounded-xl border-2 text-left transition-all ${
                                    form.audience === "specific"
                                        ? "bg-[#0C4650] text-white border-[#0C4650]"
                                        : "bg-white text-[#0C4650] border-gray-200 hover:border-[#0C4650]"
                                }`}
                            >
                                <div className="text-sm font-bold">Selezione manuale</div>
                                <div className={`text-xs mt-0.5 ${
                                    form.audience === "specific" ? "text-[#C7FF2C]" : "text-gray-500"
                                }`}>
                                    {selected.length} selezionati
                                </div>
                            </button>
                        </div>
                        {form.audience === "specific" && (
                            <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                                {agents.length === 0 ? (
                                    <div className="p-3 text-sm text-gray-400">Nessun agente attivo</div>
                                ) : agents.map((a) => (
                                    <label key={a.id}
                                           className="flex items-center gap-3 p-2.5 hover:bg-gray-50 cursor-pointer"
                                           data-testid={`broadcast-pick-${a.id}`}>
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(a.id)}
                                            onChange={() => toggleAgent(a.id)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold text-[#0C4650] truncate">{a.name}</div>
                                            <div className="text-xs text-gray-500 truncate">{a.email}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Oggetto + messaggio */}
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                Oggetto *
                            </label>
                            <input
                                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                placeholder="es. Nuova offerta XYZ disponibile"
                                value={form.subject}
                                onChange={(e) => set("subject", e.target.value)}
                                data-testid="broadcast-subject"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                Messaggio *
                            </label>
                            <textarea
                                rows={5}
                                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                placeholder="Scrivi qui il testo della comunicazione..."
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                                data-testid="broadcast-message"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Vai a capo due volte per separare i paragrafi.
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    Testo pulsante CTA (opz.)
                                </label>
                                <input
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    placeholder="es. Vai al catalogo"
                                    value={form.cta_label}
                                    onChange={(e) => set("cta_label", e.target.value)}
                                    data-testid="broadcast-cta-label"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-[#0C4650] block mb-1">
                                    URL pulsante CTA (opz.)
                                </label>
                                <input
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm text-[#0C4650] focus:border-[#0C4650] focus:outline-none bg-white"
                                    placeholder="/offerte oppure URL completo"
                                    value={form.cta_url}
                                    onChange={(e) => set("cta_url", e.target.value)}
                                    data-testid="broadcast-cta-url"
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-[#0C4650] cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.send_email}
                                onChange={(e) => set("send_email", e.target.checked)}
                                className="rounded"
                                data-testid="broadcast-send-email-toggle"
                            />
                            Invia anche via email (oltre alla notifica in-app)
                        </label>

                        {/* Allegato */}
                        <div className="border-t border-gray-100 pt-3">
                            <label className="text-xs font-bold text-[#0C4650] block mb-2">
                                Allegato (opzionale, max 25 MB)
                            </label>
                            {!attachment ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => attRef.current?.click()}
                                        disabled={uploadingAtt}
                                        className="w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-[#0C4650] hover:border-[#0C4650] hover:bg-gray-50 transition-all disabled:opacity-50"
                                        data-testid="broadcast-attach-btn"
                                    >
                                        {uploadingAtt ? (
                                            <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</>
                                        ) : (
                                            <><Paperclip className="w-4 h-4" /> Allega un file</>
                                        )}
                                    </button>
                                    <input
                                        ref={attRef}
                                        type="file"
                                        className="hidden"
                                        onChange={handleAttachment}
                                        data-testid="broadcast-attach-input"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Il link al file sarà incluso nella notifica e nell'email.
                                    </p>
                                </>
                            ) : (
                                <div className="flex items-center gap-2 p-3 bg-[#C7FF2C]/20 border-2 border-[#C7FF2C] rounded-lg"
                                     data-testid="broadcast-attachment-preview">
                                    <Paperclip className="w-5 h-5 text-[#0C4650] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm text-[#0C4650] truncate">
                                            {attachment.filename}
                                        </div>
                                        <div className="text-[11px] text-gray-600">
                                            {fmtSize(attachment.size)}
                                        </div>
                                    </div>
                                    <button onClick={removeAttachment}
                                            className="p-1.5 hover:bg-white rounded-lg"
                                            data-testid="broadcast-remove-attachment"
                                            title="Rimuovi">
                                        <X className="w-4 h-4 text-[#0C4650]" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4 flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500">
                        Verrà inviato a <b>{recipientCount}</b> agente/i
                    </div>
                    <button
                        onClick={send}
                        disabled={sending || recipientCount === 0}
                        className="btn-primary disabled:opacity-50"
                        data-testid="send-broadcast-btn"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Invia comunicazione
                    </button>
                </div>
            </div>
        </div>
    );
};

// --------- QUOTES ----------
const QuotesPanel = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("all");

    const load = () =>
        api.get("/quotes").then(({ data }) => {
            setQuotes(data);
            setLoading(false);
        });
    useEffect(() => {
        load();
    }, []);

    const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

    return (
        <div>
            <div className="flex gap-2 mb-5 overflow-x-auto">
                {[
                    { k: "all", l: "Tutte" },
                    { k: "nuovo", l: "Nuove" },
                    { k: "in_valutazione", l: "In valutazione" },
                    { k: "risposto", l: "Risposte" },
                ].map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setFilter(f.k)}
                        data-testid={`admin-quote-filter-${f.k}`}
                        className={`px-4 py-2 rounded-full text-xs font-bold ${
                            filter === f.k ? "bg-[#0C4650] text-white" : "bg-white border border-gray-200 text-[#0C4650]"
                        }`}
                    >
                        {f.l}
                    </button>
                ))}
            </div>
            {loading ? (
                <Loading />
            ) : filtered.length === 0 ? (
                <Empty text="Nessun preventivo nel filtro." />
            ) : (
                <div className="space-y-3">
                    {filtered.map((q) => (
                        <div key={q.id} className="card p-5" data-testid={`admin-quote-${q.id}`}>
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-black text-lg text-[#0C4650]">{q.client_name}</h3>
                                        <StatusBadge s={q.status} />
                                    </div>
                                    <div className="text-sm text-gray-500 mt-1">
                                        {SECTOR_LABELS[q.sector] || SECTORS[q.sector]?.label} · da {q.agent_name} ·{" "}
                                        {new Date(q.created_at).toLocaleString("it-IT")}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Link to={`/richieste/${q.id}`} className="btn-ghost" data-testid={`view-quote-${q.id}`}>
                                        Dettaglio
                                    </Link>
                                    <QuickPdfUploadButton
                                        quote={q}
                                        onUploaded={() => load()}
                                    />
                                    <button
                                        onClick={() => setSelected(q)}
                                        className="btn-primary !py-2 !px-4 !text-sm"
                                        data-testid={`upload-offer-${q.id}`}
                                    >
                                        <Upload className="w-4 h-4" /> {q.offer ? "Aggiorna offerta" : "Carica offerta"}
                                    </button>
                                </div>
                            </div>
                            <CommissionField
                                quote={q}
                                onSaved={(value) =>
                                    setQuotes((prev) =>
                                        prev.map((it) =>
                                            it.id === q.id ? { ...it, commission_total: value } : it,
                                        ),
                                    )
                                }
                            />
                        </div>
                    ))}
                </div>
            )}

            {selected && (
                <OfferUploadModal
                    quote={selected}
                    onClose={() => setSelected(null)}
                    onSaved={() => {
                        setSelected(null);
                        load();
                    }}
                />
            )}
        </div>
    );
};

// Quick PDF upload: 1 click → file picker → upload + crea offerta minimale
const QuickPdfUploadButton = ({ quote, onUploaded }) => {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset per consentire re-selezione stesso file
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
            toast.error("Carica un file PDF");
            return;
        }
        setBusy(true);
        try {
            // 1) upload del PDF nello storage
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });
            // 2) crea/aggiorna offerta con il solo allegato PDF
            await api.post(`/admin/quotes/${quote.id}/offer`, {
                title: "Offerta in PDF",
                description: "L'offerta dettagliata è disponibile nel PDF allegato.",
                attachment_url: up.url,
            });
            toast.success("PDF caricato e inviato all'agente");
            onUploaded?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Caricamento PDF fallito");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 bg-[#C7FF2C] hover:bg-[#bff21d] text-[#0C4650] font-bold !py-2 !px-4 !text-sm rounded-lg transition-all disabled:opacity-50"
                data-testid={`quick-pdf-${quote.id}`}
                title="Carica direttamente un PDF già pronto"
            >
                {busy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Paperclip className="w-4 h-4" />
                )}
                {busy ? "Caricamento…" : "Carica PDF"}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={handleFile}
                data-testid={`quick-pdf-input-${quote.id}`}
            />
        </>
    );
};

const StatusBadge = ({ s }) => {
    const map = {
        nuovo: "badge-new",
        in_valutazione: "inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-bold",
        risposto: "badge-replied",
    };
    const labels = { nuovo: "Nuovo", in_valutazione: "In valutazione", risposto: "Risposto" };
    return <span className={map[s]}>{labels[s]}</span>;
};

// Admin-editable commission slot; agent sees read-only in QuoteDetail
const CommissionField = ({ quote, onSaved }) => {
    const [value, setValue] = useState(
        quote.commission_total != null ? String(quote.commission_total) : "",
    );
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setValue(quote.commission_total != null ? String(quote.commission_total) : "");
        setDirty(false);
    }, [quote.commission_total]);

    const save = async () => {
        if (!dirty || saving) return;
        const trimmed = value.trim().replace(",", ".");
        const parsed = trimmed === "" ? null : Number(trimmed);
        if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
            toast.error("Importo non valido");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/admin/quotes/${quote.id}/commission`,
                { commission_total: parsed },
            );
            onSaved?.(data.commission_total);
            setDirty(false);
            toast.success("Provvigione salvata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Salvataggio fallito");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="mt-4 pt-4 border-t border-dashed border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid={`admin-commission-field-${quote.id}`}
        >
            <div className="inline-flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#C7FF2C] text-[#0C4650] flex items-center justify-center shrink-0">
                    <Euro className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <div className="text-[11px] tracking-[0.28em] font-bold text-[#0C4650]">
                    PROVV. TOTALE
                </div>
            </div>
            <div className="flex-1 flex items-center gap-2">
                <div className="relative flex-1 max-w-xs">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">
                        €
                    </span>
                    <input
                        type="text"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={value}
                        onChange={(e) => {
                            setValue(e.target.value);
                            setDirty(true);
                        }}
                        onBlur={save}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                            }
                        }}
                        data-testid={`admin-commission-input-${quote.id}`}
                        className="w-full pl-7 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-bold text-[#0C4650] focus:border-[#C7FF2C] focus:outline-none bg-[#f7f7f5]/60"
                    />
                </div>
                {saving && <Loader2 className="w-4 h-4 animate-spin text-[#0C4650]" />}
                {!saving && dirty && (
                    <span className="text-[11px] text-amber-600 font-bold">non salvato</span>
                )}
                {!saving && !dirty && quote.commission_total != null && (
                    <span className="text-[11px] text-green-600 font-bold">salvato</span>
                )}
            </div>
        </div>
    );
};

const OfferUploadModal = ({ quote, onClose, onSaved }) => {
    const [form, setForm] = useState(
        quote.offer || {
            title: "",
            description: "",
            price: "",
            monthly_price: "",
            attachment_url: "",
            notes: "",
        },
    );
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/quotes/${quote.id}/offer`, form);
            toast.success("Offerta caricata e agente notificato");
            onSaved();
        } catch (e) {
            toast.error("Errore caricamento");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="offer-upload-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                    <div>
                        <h3 className="text-xl font-black text-[#0C4650]">Carica offerta</h3>
                        <div className="text-sm text-gray-500">per {quote.client_name}</div>
                    </div>
                    <button onClick={onClose} className="btn-ghost" data-testid="close-offer-modal">
                        ✕
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="label">Titolo *</label>
                        <input className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-form-title" />
                    </div>
                    <div>
                        <label className="label">Descrizione *</label>
                        <textarea className="field min-h-[120px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="offer-form-description" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Rata mensile</label>
                            <input className="field" value={form.monthly_price || ""} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} placeholder="€ 299/mese" data-testid="offer-form-monthly" />
                        </div>
                        <div>
                            <label className="label">Prezzo totale</label>
                            <input className="field" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="€ 12.500" data-testid="offer-form-price" />
                        </div>
                    </div>
                    <div>
                        <label className="label">Allegato (PDF o immagine)</label>
                        <FileUploadInput
                            value={form.attachment_url}
                            onChange={(url) => setForm({ ...form, attachment_url: url })}
                        />
                    </div>
                    <div>
                        <label className="label">Note interne</label>
                        <textarea className="field" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="offer-form-notes" />
                    </div>
                </div>
                <div className="p-6 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="btn-outline">
                        Annulla
                    </button>
                    <button
                        onClick={save}
                        disabled={!form.title || !form.description || saving}
                        className="btn-primary"
                        data-testid="offer-form-save"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Carica e notifica
                    </button>
                </div>
            </div>
        </div>
    );
};

// --------- CATALOG OFFERS ----------
const OffersPanel = () => {
    const [offers, setOffers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sectorFilter, setSectorFilter] = useState("all");
    const [query, setQuery] = useState("");

    const load = () =>
        api.get("/offers").then(({ data }) => {
            setOffers(data);
            setLoading(false);
        });
    useEffect(() => {
        load();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare l'offerta?")) return;
        await api.delete(`/admin/offers/${id}`);
        toast.success("Offerta rimossa");
        load();
    };

    const counts = useMemo(() => {
        const c = { all: offers.length };
        for (const s of Object.keys(SECTORS)) c[s] = 0;
        for (const o of offers) c[o.sector] = (c[o.sector] || 0) + 1;
        return c;
    }, [offers]);

    const showSearch = sectorFilter === "noleggio_lungo_termine" || sectorFilter === "all";

    const filtered = useMemo(() => {
        let list = sectorFilter === "all"
            ? offers
            : offers.filter((o) => o.sector === sectorFilter);
        const q = query.trim().toLowerCase();
        if (q && showSearch) {
            list = list.filter((o) => {
                const haystack = [
                    o.title, o.subtitle, o.description,
                    ...(o.highlights || []),
                ].filter(Boolean).join(" ").toLowerCase();
                return haystack.includes(q);
            });
        }
        return list;
    }, [offers, sectorFilter, query, showSearch]);

    return (
        <div>
            <div className="flex flex-wrap justify-between items-center mb-5 gap-3">
                <div className="text-sm text-gray-600">
                    {filtered.length} di {offers.length} offerte
                    {sectorFilter !== "all"
                        && ` · ${SECTOR_LABELS[sectorFilter] || SECTORS[sectorFilter]?.label}`}
                </div>
                <button onClick={() => setEditing({})} className="btn-primary" data-testid="new-catalog-offer">
                    <Plus className="w-4 h-4" /> Nuova offerta
                </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-5 -mx-1" data-testid="admin-offers-sector-tabs">
                <SectorTab
                    active={sectorFilter === "all"}
                    onClick={() => setSectorFilter("all")}
                    label="Tutte"
                    count={counts.all}
                    testid="sector-tab-all"
                />
                {Object.values(SECTORS).map((s) => (
                    <SectorTab
                        key={s.key}
                        active={sectorFilter === s.key}
                        onClick={() => setSectorFilter(s.key)}
                        label={s.label}
                        count={counts[s.key] || 0}
                        testid={`sector-tab-${s.key}`}
                    />
                ))}
            </div>
            {showSearch && (
                <div className="mb-5 relative">
                    <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Cerca per modello, marca o caratteristica…"
                        className="w-full pl-12 pr-12 py-3.5 rounded-full border-2 border-gray-200 bg-white text-[#0C4650] font-medium placeholder:text-gray-400 focus:border-[#0C4650] focus:outline-none transition-all"
                        data-testid="admin-offers-search-input"
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                            data-testid="admin-offers-search-clear"
                            title="Cancella ricerca"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    )}
                </div>
            )}
            {loading ? (
                <Loading />
            ) : filtered.length === 0 ? (
                <div className="card p-12 text-center text-gray-500">
                    {query.trim()
                        ? `Nessun risultato per "${query.trim()}"`
                        : "Nessuna offerta in questa categoria."}
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((o) => (
                        <div key={o.id} className="card p-5 flex flex-col" data-testid={`admin-offer-${o.id}`}>
                            <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">
                                {(SECTOR_LABELS[o.sector] || SECTORS[o.sector]?.label)?.toUpperCase()}
                            </div>
                            <div className="font-black text-[#0C4650] text-lg mt-1">{o.title}</div>
                            <div className="text-sm text-gray-500 line-clamp-2">{o.description}</div>
                            {o.monthly_price && (
                                <div className="text-[#0C4650] font-black text-xl mt-2">{o.monthly_price}</div>
                            )}
                            <div className="mt-auto pt-3 flex gap-2">
                                <button onClick={() => setEditing(o)} className="btn-outline !py-2 !px-3 !text-xs">
                                    <Pencil className="w-3 h-3" /> Modifica
                                </button>
                                <button
                                    onClick={() => del(o.id)}
                                    data-testid={`delete-offer-${o.id}`}
                                    className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 font-bold text-xs px-3"
                                >
                                    <Trash2 className="w-3 h-3" /> Elimina
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing !== null && (
                <CatalogOfferModal
                    offer={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
};

const SectorTab = ({ active, onClick, label, count, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
            active
                ? "bg-[#0C4650] text-white"
                : "bg-white border border-gray-200 text-[#0C4650] hover:border-[#0C4650]"
        }`}
    >
        <span>{label}</span>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
            active ? "bg-[#C7FF2C] text-[#0C4650]" : "bg-[#f7f7f5] text-[#898A8D]"
        }`}>
            {count}
        </span>
    </button>
);

const CatalogOfferModal = ({ offer, onClose, onSaved }) => {
    const isEdit = !!offer.id;
    const [form, setForm] = useState({
        sector: offer.sector || "noleggio_lungo_termine",
        title: offer.title || "",
        subtitle: offer.subtitle || "",
        description: offer.description || "",
        price: offer.price || "",
        monthly_price: offer.monthly_price || "",
        image_url: offer.image_url || "",
        highlights: offer.highlights || [],
        active: offer.active ?? true,
    });
    const [hlInput, setHlInput] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/admin/offers/${offer.id}`, form);
            else await api.post("/admin/offers", form);
            toast.success(isEdit ? "Offerta aggiornata" : "Offerta creata");
            onSaved();
        } catch (e) {
            toast.error("Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="catalog-offer-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="text-xl font-black text-[#0C4650]">
                        {isEdit ? "Modifica offerta" : "Nuova offerta"}
                    </h3>
                    <button onClick={onClose} className="btn-ghost">
                        ✕
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="label">Settore</label>
                        <select className="field" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="catalog-form-sector">
                            {Object.values(SECTORS).map((s) => (
                                <option key={s.key} value={s.key}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Titolo *</label>
                            <input className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="catalog-form-title" />
                        </div>
                        <div>
                            <label className="label">Sottotitolo</label>
                            <input className="field" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className="label">Descrizione *</label>
                        <textarea className="field min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="catalog-form-description" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Rata mensile</label>
                            <input className="field" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} placeholder="€ 299/mese" />
                        </div>
                        <div>
                            <label className="label">Prezzo</label>
                            <input className="field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="€ 12.500" />
                        </div>
                    </div>
                    <div>
                        <label className="label">Immagine di copertina</label>
                        <FileUploadInput
                            value={form.image_url}
                            onChange={(url) => setForm({ ...form, image_url: url })}
                            accept=".jpg,.jpeg,.png,.webp"
                            hint="JPG, PNG, WEBP · Max 15 MB"
                        />
                    </div>
                    <div>
                        <label className="label">Punti forti</label>
                        <div className="flex gap-2">
                            <input
                                className="field"
                                value={hlInput}
                                onChange={(e) => setHlInput(e.target.value)}
                                placeholder="es. Garanzia 25 anni"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (hlInput.trim()) {
                                            setForm({ ...form, highlights: [...form.highlights, hlInput.trim()] });
                                            setHlInput("");
                                        }
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (hlInput.trim()) {
                                        setForm({ ...form, highlights: [...form.highlights, hlInput.trim()] });
                                        setHlInput("");
                                    }
                                }}
                                className="btn-outline !py-2 !px-3"
                            >
                                +
                            </button>
                        </div>
                        {form.highlights.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {form.highlights.map((h, i) => (
                                    <span
                                        key={i}
                                        className="bg-[#C7FF2C]/30 text-[#0C4650] text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-2"
                                    >
                                        {h}
                                        <button
                                            onClick={() =>
                                                setForm({
                                                    ...form,
                                                    highlights: form.highlights.filter((_, idx) => idx !== i),
                                                })
                                            }
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) => setForm({ ...form, active: e.target.checked })}
                        />
                        <span className="text-sm font-semibold text-[#0C4650]">Attiva nel catalogo</span>
                    </label>
                </div>
                <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="btn-outline">
                        Annulla
                    </button>
                    <button
                        onClick={save}
                        disabled={!form.title || !form.description || saving}
                        className="btn-primary"
                        data-testid="catalog-form-save"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------- Helpers ----------
const Section = ({ title, children, testid }) => (
    <div data-testid={testid}>
        <h2 className="text-lg font-bold text-[#0C4650] mb-3">{title}</h2>
        <div className="space-y-3">{children}</div>
    </div>
);
const Loading = () => <div className="py-10 text-center text-gray-400">Caricamento…</div>;
const Empty = ({ text }) => <div className="card p-8 text-center text-gray-500">{text}</div>;

// ---------- Energy Offers Panel ----------
const EnergyOffersPanel = () => {
    const [offers, setOffers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [thresholdsOpen, setThresholdsOpen] = useState(false);

    const load = () =>
        api.get("/energy/offers").then(({ data }) => {
            setOffers(data);
            setLoading(false);
        });
    useEffect(() => {
        load();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Eliminare l'offerta energia?")) return;
        await api.delete(`/admin/energy-offers/${id}`);
        toast.success("Offerta rimossa");
        load();
    };

    const filtered = filter === "all" ? offers : offers.filter((o) => o.tipo === filter);

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div className="flex gap-2">
                    {[
                        { k: "all", l: "Tutte" },
                        { k: "luce", l: "Luce" },
                        { k: "gas", l: "Gas" },
                        { k: "dual", l: "Dual" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`energy-filter-${f.k}`}
                            className={`px-4 py-2 rounded-full text-xs font-bold ${
                                filter === f.k
                                    ? "bg-[#0C4650] text-white"
                                    : "bg-white border border-gray-200 text-[#0C4650]"
                            }`}
                        >
                            {f.l}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setThresholdsOpen(true)}
                        className="btn-outline !py-2 !px-3 !text-xs"
                        data-testid="open-bill-thresholds"
                    >
                        ⚙ Validazione bollette
                    </button>
                    <button
                        onClick={() => setEditing({})}
                        className="btn-primary"
                        data-testid="new-energy-offer"
                    >
                        <Plus className="w-4 h-4" /> Nuova offerta energia
                    </button>
                </div>
            </div>

            {thresholdsOpen && (
                <BillThresholdsModal onClose={() => setThresholdsOpen(false)} />
            )}
            {loading ? (
                <Loading />
            ) : filtered.length === 0 ? (
                <Empty text="Nessuna offerta energia nel catalogo." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((o) => (
                        <div
                            key={o.id}
                            className="card p-5 flex flex-col"
                            data-testid={`admin-energy-offer-${o.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-1.5 text-[10px] tracking-widest font-bold text-[#898A8D]">
                                    {o.tipo === "gas" ? (
                                        <span className="inline-flex items-center gap-1 text-amber-600">
                                            <Zap className="w-3 h-3" /> GAS
                                        </span>
                                    ) : o.tipo === "dual" ? (
                                        <span className="inline-flex items-center gap-1 text-[#0C4650]">
                                            LUCE + GAS
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-[#0C4650]">
                                            <Zap className="w-3 h-3" /> LUCE
                                        </span>
                                    )}
                                </div>
                                {o.green_energy && (
                                    <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[9px] font-bold px-2 py-0.5 rounded-full">
                                        <Leaf className="w-2.5 h-2.5" /> GREEN
                                    </span>
                                )}
                            </div>
                            <div className="mt-1 text-[#0C4650] font-black text-base">
                                {o.operatore}
                            </div>
                            <div className="text-sm text-gray-600">{o.plan_name}</div>

                            <div className="mt-3 flex items-baseline gap-2">
                                <span className="text-2xl font-black text-[#0C4650]">
                                    € {(o.tipo === "gas" ? o.price_per_smc : o.price_per_kwh)?.toFixed(3)}
                                </span>
                                <span className="text-xs text-gray-500">
                                    /{o.tipo === "gas" ? "Smc" : "kWh"}
                                </span>
                            </div>
                            <div className="text-xs text-gray-600">
                                + € {o.monthly_fee?.toFixed(2)} fissa/mese
                            </div>

                            {o.promo_label && (
                                <div className="mt-2 inline-block text-[10px] font-bold bg-[#C7FF2C]/40 text-[#0C4650] px-2 py-1 rounded-full w-fit">
                                    {o.promo_label}
                                </div>
                            )}

                            <div className="mt-auto pt-4 flex gap-2">
                                <button
                                    onClick={() => setEditing(o)}
                                    className="btn-outline !py-2 !px-3 !text-xs"
                                    data-testid={`edit-energy-${o.id}`}
                                >
                                    <Pencil className="w-3 h-3" /> Modifica
                                </button>
                                <button
                                    onClick={() => del(o.id)}
                                    data-testid={`delete-energy-${o.id}`}
                                    className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 font-bold text-xs px-3"
                                >
                                    <Trash2 className="w-3 h-3" /> Elimina
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing !== null && (
                <EnergyOfferModal
                    offer={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
};

const BillThresholdsModal = ({ onClose }) => {
    const [values, setValues] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/bill-thresholds").then(({ data }) => setValues(data));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/bill-thresholds", {
                min_size_bytes: parseInt(values.min_size_bytes, 10),
                min_img_dim: parseInt(values.min_img_dim, 10),
                min_variance: parseFloat(values.min_variance),
            });
            setValues(data);
            toast.success("Soglie aggiornate");
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const reset = () =>
        setValues({ min_size_bytes: 3000, min_img_dim: 400, min_variance: 120 });

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            data-testid="bill-thresholds-modal"
        >
            <div className="bg-white rounded-2xl max-w-md w-full">
                <div className="p-6 border-b">
                    <h3 className="text-xl font-black text-[#0C4650]">
                        Soglie validazione bollette
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                        Impostazioni attive in tempo reale (non serve riavvio). Abbassa la
                        <span className="font-bold"> varianza</span> se bollette reali vengono
                        scartate come "uniformi".
                    </p>
                </div>
                {!values ? (
                    <div className="py-10 text-center text-gray-400">Caricamento…</div>
                ) : (
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="label">Dimensione minima file (byte)</label>
                            <input
                                type="number"
                                className="field"
                                value={values.min_size_bytes}
                                onChange={(e) =>
                                    setValues({ ...values, min_size_bytes: e.target.value })
                                }
                                data-testid="threshold-min-size"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Default 3000. File sotto questa soglia → 400 "troppo piccolo".
                            </p>
                        </div>
                        <div>
                            <label className="label">Lato minimo immagine (pixel)</label>
                            <input
                                type="number"
                                className="field"
                                value={values.min_img_dim}
                                onChange={(e) =>
                                    setValues({ ...values, min_img_dim: e.target.value })
                                }
                                data-testid="threshold-min-dim"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Default 400. Altezza e larghezza devono essere ≥ di questo valore.
                            </p>
                        </div>
                        <div>
                            <label className="label">Varianza minima (uniformità)</label>
                            <input
                                type="number"
                                step="0.1"
                                className="field"
                                value={values.min_variance}
                                onChange={(e) =>
                                    setValues({ ...values, min_variance: e.target.value })
                                }
                                data-testid="threshold-min-variance"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Default 120. Più basso = accetta anche immagini meno
                                contrastate. Tarala su 50–100 se bollette reali vengono scartate.
                            </p>
                        </div>
                    </div>
                )}
                <div className="p-6 border-t flex justify-between gap-3">
                    <button
                        onClick={reset}
                        className="btn-ghost text-xs"
                        data-testid="threshold-reset"
                    >
                        Ripristina default
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="btn-outline">
                            Chiudi
                        </button>
                        <button
                            onClick={save}
                            disabled={!values || saving}
                            className="btn-primary"
                            data-testid="threshold-save"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Salva
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


const EnergyOfferModal = ({ offer, onClose, onSaved }) => {
    const isEdit = !!offer.id;
    const [form, setForm] = useState({
        operatore: offer.operatore || "",
        plan_name: offer.plan_name || "",
        tipo: offer.tipo || "luce",
        price_per_kwh: offer.price_per_kwh ?? "",
        price_per_smc: offer.price_per_smc ?? "",
        monthly_fee: offer.monthly_fee ?? "",
        promo_label: offer.promo_label || "",
        green_energy: offer.green_energy || false,
        features: offer.features || [],
        image_url: offer.image_url || "",
        active: offer.active ?? true,
    });
    const [featInput, setFeatInput] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                price_per_kwh: form.price_per_kwh === "" ? null : parseFloat(form.price_per_kwh),
                price_per_smc: form.price_per_smc === "" ? null : parseFloat(form.price_per_smc),
                monthly_fee:
                    form.monthly_fee === "" ? 0 : parseFloat(form.monthly_fee),
            };
            if (isEdit) await api.patch(`/admin/energy-offers/${offer.id}`, payload);
            else await api.post("/admin/energy-offers", payload);
            toast.success(isEdit ? "Offerta aggiornata" : "Offerta creata");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            data-testid="energy-offer-modal"
        >
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="text-xl font-black text-[#0C4650]">
                        {isEdit ? "Modifica offerta energia" : "Nuova offerta energia"}
                    </h3>
                    <button onClick={onClose} className="btn-ghost">
                        ✕
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label">Operatore *</label>
                            <input
                                className="field"
                                value={form.operatore}
                                onChange={(e) => setForm({ ...form, operatore: e.target.value })}
                                placeholder="Es. Enel Energia"
                                data-testid="energy-form-operatore"
                            />
                        </div>
                        <div>
                            <label className="label">Nome piano *</label>
                            <input
                                className="field"
                                value={form.plan_name}
                                onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                                placeholder="Es. Flex Luce"
                                data-testid="energy-form-plan"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="label">Tipo *</label>
                        <div className="grid grid-cols-3 gap-2">
                            {["luce", "gas", "dual"].map((t) => (
                                <button
                                    type="button"
                                    key={t}
                                    onClick={() => setForm({ ...form, tipo: t })}
                                    data-testid={`energy-form-tipo-${t}`}
                                    className={`py-2 rounded-lg font-bold text-sm uppercase ${
                                        form.tipo === t
                                            ? "bg-[#0C4650] text-white"
                                            : "bg-white border border-gray-200 text-[#0C4650]"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        {(form.tipo === "luce" || form.tipo === "dual") && (
                            <div>
                                <label className="label">€/kWh</label>
                                <input
                                    className="field"
                                    value={form.price_per_kwh}
                                    onChange={(e) => setForm({ ...form, price_per_kwh: e.target.value })}
                                    inputMode="decimal"
                                    placeholder="0.142"
                                    data-testid="energy-form-kwh"
                                />
                            </div>
                        )}
                        {(form.tipo === "gas" || form.tipo === "dual") && (
                            <div>
                                <label className="label">€/Smc</label>
                                <input
                                    className="field"
                                    value={form.price_per_smc}
                                    onChange={(e) => setForm({ ...form, price_per_smc: e.target.value })}
                                    inputMode="decimal"
                                    placeholder="0.42"
                                    data-testid="energy-form-smc"
                                />
                            </div>
                        )}
                        <div>
                            <label className="label">Quota fissa €/mese</label>
                            <input
                                className="field"
                                value={form.monthly_fee}
                                onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                                inputMode="decimal"
                                placeholder="12.00"
                                data-testid="energy-form-fixed"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="label">Etichetta promo</label>
                        <input
                            className="field"
                            value={form.promo_label}
                            onChange={(e) => setForm({ ...form, promo_label: e.target.value })}
                            placeholder="Es. Prezzo bloccato 12 mesi"
                            data-testid="energy-form-promo"
                        />
                    </div>
                    <div>
                        <label className="label">Immagine di copertina</label>
                        <FileUploadInput
                            value={form.image_url}
                            onChange={(url) => setForm({ ...form, image_url: url })}
                            accept=".jpg,.jpeg,.png,.webp"
                            hint="JPG, PNG, WEBP · Max 15 MB"
                        />
                    </div>
                    <div>
                        <label className="label">Caratteristiche</label>
                        <div className="flex gap-2">
                            <input
                                className="field"
                                value={featInput}
                                onChange={(e) => setFeatInput(e.target.value)}
                                placeholder="Es. App dedicata"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && featInput.trim()) {
                                        e.preventDefault();
                                        setForm({
                                            ...form,
                                            features: [...form.features, featInput.trim()],
                                        });
                                        setFeatInput("");
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (featInput.trim()) {
                                        setForm({
                                            ...form,
                                            features: [...form.features, featInput.trim()],
                                        });
                                        setFeatInput("");
                                    }
                                }}
                                className="btn-outline !py-2 !px-3"
                            >
                                +
                            </button>
                        </div>
                        {form.features.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {form.features.map((f, i) => (
                                    <span
                                        key={i}
                                        className="bg-[#C7FF2C]/30 text-[#0C4650] text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-2"
                                    >
                                        {f}
                                        <button
                                            onClick={() =>
                                                setForm({
                                                    ...form,
                                                    features: form.features.filter((_, idx) => idx !== i),
                                                })
                                            }
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.green_energy}
                                onChange={(e) => setForm({ ...form, green_energy: e.target.checked })}
                            />
                            <span className="text-sm font-semibold text-[#0C4650]">
                                🌱 Energia verde
                            </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                            />
                            <span className="text-sm font-semibold text-[#0C4650]">Attiva</span>
                        </label>
                    </div>
                </div>
                <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="btn-outline">
                        Annulla
                    </button>
                    <button
                        onClick={save}
                        disabled={!form.operatore || !form.plan_name || saving}
                        className="btn-primary"
                        data-testid="energy-form-save"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        Salva
                    </button>
                </div>
            </div>
        </div>
    );
};

const FileUploadInput = ({ value, onChange, accept = ".pdf,.jpg,.jpeg,.png,.webp", hint = "Max 15 MB · PDF, JPG, PNG, WEBP" }) => {
    const [uploading, setUploading] = useState(false);
    const [filename, setFilename] = useState("");

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const backend = process.env.REACT_APP_BACKEND_URL;
            onChange(`${backend}${data.url}`);
            setFilename(data.filename);
            toast.success("File caricato");
        } catch (err) {
            toast.error("Upload fallito");
        } finally {
            setUploading(false);
        }
    };

    const isImage = value && /\.(jpg|jpeg|png|webp)(\?|$)/i.test(value);
    const previewUrl = value && value.includes("/api/files/")
        ? `${value}${value.includes("?") ? "&" : "?"}token=${encodeURIComponent(localStorage.getItem("scintilla_token") || "")}`
        : value;

    return (
        <div className="space-y-2">
            {value ? (
                <div className="p-3 bg-[#f7f7f5] rounded-lg" data-testid="attachment-preview">
                    {isImage && (
                        <div
                            className="w-full h-32 rounded-lg bg-cover bg-center mb-3 border border-gray-200"
                            style={{ backgroundImage: `url(${previewUrl})` }}
                        />
                    )}
                    <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-[#0C4650]" />
                        <span className="flex-1 text-sm truncate text-[#0C4650] font-semibold">
                            {filename || (isImage ? "Immagine caricata" : "Allegato caricato")}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                onChange("");
                                setFilename("");
                            }}
                            className="text-rose-500 hover:text-rose-700"
                            data-testid="remove-attachment"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            ) : (
                <label
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-[#0C4650] rounded-xl py-6 px-4 cursor-pointer transition-colors"
                    data-testid="attachment-dropzone"
                >
                    <Upload className="w-5 h-5 text-[#0C4650]" />
                    <span className="text-sm font-semibold text-[#0C4650]">
                        {uploading ? "Caricamento..." : "Clicca per caricare il file"}
                    </span>
                    <span className="text-xs text-gray-400">{hint}</span>
                    <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={handleFile}
                        disabled={uploading}
                        data-testid="attachment-file-input"
                    />
                </label>
            )}
        </div>
    );
};
